// Eskom PARCC — shared transaction-grain gap classifiers.
//
// Extracted verbatim from the report's deckSections so the dashboard, the report
// and the finding summary all decide "is this a gap / self-declared / still
// outstanding / remediated" on ONE definition (no drift between surfaces). The
// classifiers read the transaction's compliance level (callers pass the EFFECTIVE
// level — findings + override rolled up — so the answer matches the register) and
// its gap status. Pure; unit-tested in isolation.
import type { Transaction } from '../data/types';
import { normalizeGapStatus } from './normalize';

export type GapTier = 'PFMA' | 'Policy & Procedures' | 'Housekeeping';

/**
 * Classify a compliance level into the three gap tiers (null = not a gap).
 * Covers the original register levels AND the newer taxonomy per the NTCSA-legend
 * associations (9 Jun): Possible IE / Possible Non-Compliance → PFMA; bare
 * Non-Conformance → Policy & Procedures; Administrative Control Weakness →
 * Housekeeping. Order matters — the specific phrases are tested before the bare
 * "non-conformance" catch.
 */
export function gapTierOf(level: string | undefined | null): GapTier | null {
  const n = String(level ?? '').toLowerCase();
  if (n.includes('possible ie') || n.includes('possible non-compliance')) return 'PFMA';
  if (n.includes('housekeeping') || n.includes('administrative control')) return 'Housekeeping';
  if (n.includes('policy & procedures') || n.includes('non-conformance')) return 'Policy & Procedures';
  return null;
}

/** A transaction carries a gap when its compliance level maps to a gap tier. */
export function hasGap(t: Transaction): boolean {
  return gapTierOf(t.complianceLevel) !== null;
}

/**
 * True when a compliance LEVEL is a self-declaration.
 *
 * The one definition of the level test, so the dashboard (which works on levels)
 * and {@link isSelfDeclared} (which works on transactions) cannot drift. Lindiwe's
 * rule, 27 Jul 2026: a self-declared transaction is one the buyer could not fix and
 * resolved by declaring it — the gap is closed, but the transaction KEEPS its tier
 * ("self-declared possible IE / policies and procedures / housekeeping"), and it is
 * reported SEPARATELY from non-compliant, which means not yet resolved.
 */
export function isSelfDeclaredLevel(level: string | undefined | null): boolean {
  return String(level ?? '').startsWith('Self-Declared:');
}

/** The reviewer's explicit in-app verdict on a transaction: the strict-rule override —
 *  what every live editor writes. The stored level alone is an import / Manage value and
 *  keeps the ported sheet conventions (`calculateTransactionCompliance` reads it the same way). */
function explicitVerdict(t: Transaction): string {
  return String(t.complianceLevelOverride ?? '').trim();
}

/** A terminal or lifecycle verdict — 'Compliant', 'Deleted', 'To delete' — leaves nothing
 *  to self-declare: no tier is retained and the transaction is reported elsewhere
 *  (Compliant / Anomalies). */
function isTerminalVerdict(v: string): boolean {
  return v === 'Compliant' || v === 'Deleted' || v === 'To delete';
}

/**
 * True when the gap has been self-declared — by the stored level, by the reviewer's
 * "Strict Rule" compliance-level OVERRIDE, or by the gap status. Reading the override
 * (a durable reviewer decision with no finding input) keeps this cycle-free while
 * catching transactions self-declared only via the override — which the finding-grain
 * surfaces would otherwise miss (measured: 5 NTCSA FY27 Q1 refs, 10 Sep 2026).
 */
export function isSelfDeclared(t: Transaction): boolean {
  // An explicit 'Compliant' (or 'Deleted' / 'To delete') override is the reviewer's later,
  // terminal decision: it clears a self-declaration however it was recorded (stored level,
  // gap status). This is the same rule `calculateTransactionCompliance` applies to the
  // effective level, so the register, dashboard, deck and finding dispositions agree
  // (Lindiwe, 29 Sep 2026): a Compliant row counts Compliant everywhere and a flagged row
  // is an Anomaly everywhere, never a self-declaration on one surface and not the other.
  if (isTerminalVerdict(explicitVerdict(t))) return false;
  return (
    isSelfDeclaredLevel(t.complianceLevel) ||
    isSelfDeclaredLevel(t.complianceLevelOverride) ||
    normalizeGapStatus(t.gapsStatus) === 'Closed by Self-Declaration'
  );
}

/**
 * The referenceNos of the self-declared transactions — the join a finding-count
 * surface passes to `findingDisposition` so a self-declared transaction clears all its
 * findings (Lindiwe, 16 Aug 2026). Read from each transaction's stored compliance level
 * / gap status (a durable reviewer decision), so it never depends on finding-closure
 * roll-up: no findings→transaction→findings cycle. Soft-deleted rows are skipped.
 */
export function buildSelfDeclaredRefs(transactions: readonly Transaction[]): Set<string> {
  const refs = new Set<string>();
  for (const t of transactions) {
    if (!t.deleted && t.referenceNo && isSelfDeclared(t)) refs.add(t.referenceNo);
  }
  return refs;
}

/** An outstanding gap: a gap that is neither closed/compliant nor self-declared. */
export function isOutstanding(t: Transaction): boolean {
  return (
    hasGap(t) &&
    !isSelfDeclared(t) &&
    normalizeGapStatus(t.gapsStatus) !== 'Closed' &&
    t.complianceLevel !== 'Compliant'
  );
}

/**
 * True when a transaction that HAD a gap has since been addressed — self-declared,
 * gap status Closed, or rolled up to Compliant/Not-Applicable. This is the
 * transaction-level closure signal a finding inherits (a finding is closed when
 * its own status is closed OR its transaction has been remediated).
 */
export function txRemediated(t: Transaction): boolean {
  return hasGap(t) && !isOutstanding(t);
}
